"use client";

import { useRouter, useSearchParams } from "next/navigation";
import { Suspense, useEffect, useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import { ApiError, api, saveTokens } from "@/lib/api";
import { SiteHeader } from "@/components/SiteHeader";
import { GoogleSignInButton } from "@/components/GoogleSignInButton";

interface FormState {
  email: string;
  username: string;
  password: string;
  inviteCode: string;
}

const USERNAME_RE = /^[a-zA-Z0-9_]+$/;
const INVITE_RE = /^[A-Z0-9]{8}$/;

function validate(form: FormState, t: TFunction): string | null {
  if (!form.email.includes("@")) return t("signup.errInvalidEmail");
  if (form.username.length < 3) return t("signup.errUsernameShort");
  if (!USERNAME_RE.test(form.username)) return t("signup.errUsernameChars");
  if (form.password.length < 8) return t("signup.errPasswordShort");
  if (form.inviteCode && !INVITE_RE.test(form.inviteCode))
    return t("signup.errInviteFormat");
  return null;
}

// Next.js 16 + Turbopack requires useSearchParams() to be inside a
// Suspense boundary for the static prerender to bail-out cleanly.
// Wrapping the inner form keeps the SiteHeader rendering at SSR
// while the ?invite= prefill resolves on the client.
export default function SignupPage() {
  return (
    <Suspense fallback={<SignupLoadingFallback />}>
      <SignupPageInner />
    </Suspense>
  );
}

function SignupLoadingFallback() {
  const { t } = useTranslation();
  return (
    <>
      <SiteHeader />
      <main className="mx-auto max-w-md p-8 text-sm text-[var(--color-text3)]">
        {t("signup.loading")}
      </main>
    </>
  );
}

function SignupPageInner() {
  const { t } = useTranslation();
  const router = useRouter();
  const searchParams = useSearchParams();
  // Country is no longer collected at signup. Region resolution
  // falls back to the Cloudflare CF-IPCountry header server-side;
  // explicit country self-declaration moved to KYC Tier 1
  // (POST /v1/me/kyc/tier1, gates fiat deposits via @RequireKycTier(1)
  // on /v1/me/deposit/init). EPIC-050 STORY-2200.
  const [form, setForm] = useState<FormState>({
    email: "",
    username: "",
    password: "",
    inviteCode: "",
  });
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // Pre-fill the invite code from `?invite=ABC12DEF` so shareable
  // invite links work without manual copy-paste. Uppercased to
  // match server normalization. Runs once on mount.
  useEffect(() => {
    const inviteFromUrl = searchParams.get("invite");
    if (inviteFromUrl) {
      const normalized = inviteFromUrl.trim().toUpperCase();
      if (INVITE_RE.test(normalized)) {
        // eslint-disable-next-line react-hooks/set-state-in-effect
        setForm((prev) => ({ ...prev, inviteCode: normalized }));
      }
    }
  }, [searchParams]);

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);

    const validationError = validate(form, t);
    if (validationError) {
      setError(validationError);
      return;
    }

    setSubmitting(true);
    try {
      const tokens = await api.signup({
        email: form.email,
        username: form.username,
        password: form.password,
        inviteCode: form.inviteCode || undefined,
      });
      saveTokens(tokens);
      // 2026-04-29 — Stocktwits-style first-time-connect onboarding.
      // Bootstraps the follow graph so the feed isn't a cold-start
      // empty state on first login. The page itself has a "Skip" link
      // so the funnel impact is opt-in.
      router.push("/onboarding/follow");
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message);
      } else {
        setError(t("signup.errNetwork"));
      }
      setSubmitting(false);
    }
  }

  function update<K extends keyof FormState>(key: K, value: FormState[K]) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  return (
    <>
      <SiteHeader />
      <main className="mx-auto flex w-full max-w-md flex-1 flex-col justify-center px-6 py-16">
        <h1 className="text-3xl font-bold tracking-tight">
          {t("signup.title")}
        </h1>
        <p className="mt-2 text-sm text-[var(--color-text2)]">
          {t("signup.tagline")}
        </p>

        <form onSubmit={handleSubmit} className="mt-8 flex flex-col gap-4">
          <Field
            label={t("signup.email")}
            type="email"
            value={form.email}
            onChange={(v) => update("email", v)}
            autoComplete="email"
            required
          />
          <Field
            label={t("signup.username")}
            type="text"
            value={form.username}
            onChange={(v) => update("username", v)}
            autoComplete="username"
            hint={t("signup.usernameHint")}
            required
          />
          <Field
            label={t("signup.password")}
            type="password"
            value={form.password}
            onChange={(v) => update("password", v)}
            autoComplete="new-password"
            hint={t("signup.passwordHint")}
            required
          />
          <Field
            label={t("signup.inviteCode")}
            type="text"
            value={form.inviteCode}
            onChange={(v) => update("inviteCode", v.toUpperCase())}
            autoComplete="off"
            maxLength={8}
            hint={t("signup.inviteCodeHint")}
          />

          {error && (
            <div
              role="alert"
              className="rounded-md border px-3 py-2 text-sm"
              style={{
                borderColor: "color-mix(in srgb, var(--color-status-negative) 50%, transparent)",
                background: "color-mix(in srgb, var(--color-status-negative) 8%, transparent)",
                color: "var(--color-status-negative)",
              }}
            >
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={submitting}
            className="mt-2 inline-flex h-12 items-center justify-center rounded-md px-6 font-semibold text-white transition-colors hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60"
            style={{ background: "var(--color-brand)" }}
          >
            {submitting ? t("signup.submitting") : t("signup.submit")}
          </button>

          {/* STORY-106 — OAuth divider + Continue with Google */}
          <div className="my-2 flex items-center gap-3 text-xs text-[var(--color-text3)]">
            <span className="h-px flex-1 bg-[var(--color-border)]" />
            <span>{t("auth.or")}</span>
            <span className="h-px flex-1 bg-[var(--color-border)]" />
          </div>
          <GoogleSignInButton />
        </form>

        <p className="mt-6 text-center text-xs text-[var(--color-text3)]">
          {t("signup.footer")}
        </p>
      </main>
    </>
  );
}

interface FieldProps {
  label: string;
  type: string;
  value: string;
  onChange: (value: string) => void;
  autoComplete?: string;
  hint?: string;
  required?: boolean;
  maxLength?: number;
}

function Field({
  label,
  type,
  value,
  onChange,
  autoComplete,
  hint,
  required,
  maxLength,
}: FieldProps) {
  return (
    <label className="flex flex-col gap-1.5 text-sm">
      <span className="font-medium text-[var(--color-text2)]">{label}</span>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        autoComplete={autoComplete}
        required={required}
        maxLength={maxLength}
        className="h-11 rounded-md border border-[var(--color-border)] bg-[var(--color-bg)] px-3 text-[var(--color-text)] outline-none transition-colors focus:border-[var(--color-brand)]"
      />
      {hint && <span className="text-xs text-[var(--color-text3)]">{hint}</span>}
    </label>
  );
}
